import {
  Button,
  DraggableWindowHeader,
  TextInput,
  useWindowSize,
} from "@hoylu/client-common";
import React, { type FormEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { GlobalAccess } from "../../services/workspaces/global.access";
import { isPasswordRequired as selectIsPasswordRequired } from "../../state/user/user.selector";
import type { MyWorkspaceMetadata } from "../../state/workspaces/types";
import * as workspacesActions from "../../state/workspaces/workspaces.actions";
import { selectSelectedWorkspace } from "../../state/workspaces/workspaces.selector";
import { defaultGlobalAccess } from "../../utils/create.workspace.utils";
import { useI18n } from "../../utils/hooks/use.i18n";
import { useTextInputLocalization } from "../../utils/useTextInputLocalization";
import Styles from "./DuplicateWorkspaceDialog.module.css";
import { Modal } from "./Modal";

const SCREEN_LG = 1024;

export const DuplicateWorkspaceDialog = () => {
  const dispatch = useDispatch();

  const t = useI18n("DUPLICATE_WORKSPACE_DIALOG.");

  const selectedWorkspace = useSelector(selectSelectedWorkspace);
  const isPasswordRequired = useSelector(selectIsPasswordRequired);

  if (!selectedWorkspace) return null;

  return (
    <DuplicateWorkspaceBaseDialog
      apply={(params) =>
        dispatch(
          workspacesActions.duplicateWorkspace.request({
            workspaceId: selectedWorkspace.workspaceId,
            containerId: selectedWorkspace.containerId ?? undefined,
            ...params,
          }),
        )
      }
      cancel={() => dispatch(workspacesActions.cancelDashboardOption())}
      icon="hoylu-ui-icons-copy"
      isPasswordRequired={isPasswordRequired}
      selectedWorkspace={selectedWorkspace}
      strings={{
        shortTitle: t("COPY_THIS_WORKSPACE"),
        longTitle: `${t("MAKE_COPY")} "${selectedWorkspace.workspaceName}"`,
        newWorkspaceNameLabel: t("NEW_WORKSPACE_NAME"),
        newWorkspaceNameTooltip: t("NEW_WORKSPACE_NAME"),
        newWorkspaceNameDefault: `${t("COPY_OF")} "${
          selectedWorkspace.workspaceName
        }"`,
        passwordLabel: t("PASSWORD"),
        passwordTooltip: t("PASSWORD"),
        applyButtonText: t("DUPLICATE"),
        cancelButtonText: t("CANCEL"),
        newPasswordLabel: t("NEW_PASSWORD"),
        showPassword: t("SHOW_PASSWORD"),
        characters: t("CHARACTERS"),
      }}
    />
  );
};

type ApplyParams = {
  workspaceName: string;
  globalAccess: GlobalAccess;
  password?: string;
  newPassword?: string;
};

export type DuplicateWorkspaceDialogProps = {
  alternate?: () => void;
  apply: (params: ApplyParams) => void;
  cancel: () => void;
  icon?: string;
  isPasswordRequired: boolean;
  selectedWorkspace: MyWorkspaceMetadata;
  strings: {
    alternateButtonText?: string;
    alternateButtonTooltip?: string;
    applyButtonText: string;
    cancelButtonText: string;
    characters: string;
    longTitle: string;
    newPasswordLabel: string;
    newWorkspaceNameDefault: string;
    newWorkspaceNameLabel: string;
    newWorkspaceNameTooltip: string;
    passwordLabel: string;
    passwordTooltip: string;
    shortTitle: string;
    showPassword: string;
  };
};

const DuplicateWorkspaceBaseDialog: React.FC<DuplicateWorkspaceDialogProps> = ({
  alternate,
  apply,
  cancel,
  icon,
  isPasswordRequired,
  selectedWorkspace,
  strings,
}) => {
  const tClose = useI18n("CLOSE_BUTTON.");
  const localizationDict = useTextInputLocalization();

  const [newPassword, setNewPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newName, setNewName] = useState(strings.newWorkspaceNameDefault);

  const { width: windowWidth } = useWindowSize();

  const title =
    windowWidth > SCREEN_LG ? strings.longTitle : strings.shortTitle;

  const submitAction = (e: FormEvent) => {
    e.preventDefault();
    apply({
      workspaceName: newName || "",
      password: currentPassword,
      globalAccess: defaultGlobalAccess,
      newPassword,
    });
  };

  return (
    <Modal>
      <div className={Styles.duplicateWorkspaceDialog}>
        <DraggableWindowHeader
          close={tClose("CLOSE")}
          icon={icon}
          onClose={cancel}
          title={title}
        />
        <form className={Styles.form} onSubmit={submitAction}>
          <TextInput
            isFocus
            label={strings.newWorkspaceNameLabel}
            onChange={setNewName}
            onFocus={(e) => e.target.select()}
            toolTip={strings.newWorkspaceNameTooltip}
            value={newName}
          />
          {selectedWorkspace.hasPassword && (
            <TextInput
              label={strings.passwordLabel}
              localizationDict={localizationDict}
              onChange={setCurrentPassword}
              toolTip={strings.passwordTooltip}
              type="password"
              useTogglePassword
              value={currentPassword}
            />
          )}
          {isPasswordRequired && (
            <TextInput
              label={`${strings.newPasswordLabel} - ${strings.characters}`}
              localizationDict={localizationDict}
              onChange={setNewPassword}
              type="password"
              useTogglePassword
              value={newPassword}
            />
          )}
          <div className={Styles.footer}>
            {strings.alternateButtonText && alternate && (
              <Button
                onClick={alternate}
                toolTip={strings.alternateButtonTooltip}
                type="secondary"
              >
                {strings.alternateButtonText}
              </Button>
            )}
            <Button
              onClick={cancel}
              toolTip={strings.cancelButtonText}
              type="negative"
            >
              {strings.cancelButtonText}
            </Button>
            <Button
              onClick={submitAction}
              toolTip={strings.applyButtonText}
              type="primary"
            >
              {strings.applyButtonText}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default DuplicateWorkspaceDialog;
